import {BrowserModule} from '@angular/platform-browser';
import {ReactiveFormsModule, FormsModule} from '@angular/forms';
import {NgModule, ErrorHandler} from '@angular/core';
import {HttpClientModule, HTTP_INTERCEPTORS} from '@angular/common/http';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {MatDialogModule} from '@angular/material/dialog';
import {MatTableModule} from '@angular/material/table';
import {MatPaginatorModule} from '@angular/material/paginator';
import {MatSortModule} from '@angular/material/sort';
import {MatIconModule} from '@angular/material/icon';
import {MatCardModule} from '@angular/material/card';
import {MatSelectModule} from '@angular/material/select';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {LoginComponent} from './shared/login/login.component';
import {ResetComponent} from './shared/reset/reset.component';
import {HeaderComponent} from './shared/header/header.component';
import {FooterComponent} from './shared/footer/footer.component';
import {PaginationComponent} from './shared/pagination/pagination.component';
import {AlertDialogComponent} from './shared/alert-dialog/alert-dialog.component';
import {ProbaModeDialogComponent} from './shared/proba-mode/proba-mode.dialog';
import {TermsDialogComponent} from './shared/terms-dialog/terms-dialog.component';
import {ConditionsDialogComponent} from './shared/conditions-dialog/conditions-dialog.component';
import {VideoDialogComponent} from './shared/video-dialog/video-dialog.component';
import {NgxFileDropModule} from 'ngx-file-drop';
import {GoogleChartsModule} from 'angular-google-charts';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {MDBBootstrapModule} from 'angular-bootstrap-md';
import {NgCircleProgressModule} from 'ng-circle-progress';
import {NgxPaginationModule} from 'ngx-pagination';
import {EnterpriseDatasetUploadComponent} from './enterprise/dataset-upload/dataset-upload.component';
import {EnterpriseDatasetConfigurationComponent} from './enterprise/dataset-configuration/dataset-configuration.component';
import {EnterpriseGenerateInsightsComponent} from './enterprise/generate-insights/generate-insights.component';
import {EnterpriseInsightDetailsComponent} from './enterprise/insight-details/insight-details.component';
import {EnterpriseDatasetsComponent} from './enterprise/enterprise-datasets/enterprise-datasets.component';
import {EnterpriseMlModelsComponent} from './enterprise/enterprise-mlmodels/enterprise-mlmodels.component';
import {EnterpriseInsightsComponent} from './enterprise/enterprise-insights/enterprise-insights.component';
import {SharedDatasetsComponent} from './shared/datasets/datasets.component';
import {PredictionsListComponent} from './vendor/predictions/list/predictions-list.component';
import {PredictionsUploadComponent} from './vendor/predictions/upload/predictions-upload.component';
import {FilterPipe} from './utils/filter.pipe';
import {TrustiiInterceptor} from './utils/trustii.interceptor';
import {SupportComponent} from './shared/support/support.component';
import {TestModelComponent} from './enterprise/test-model/test-model.component';
import {SignupComponent} from './shared/signup/signup.component';
import {RecaptchaModule, RecaptchaFormsModule} from 'ng-recaptcha';
import {TermsComponent} from './shared/terms/terms.component';
import {TermsOrganisationComponent} from './shared/terms-organisation/terms-organisation.component';
import {PrivacyComponent} from './shared/privacy/privacy.component';
import {PrivacyOrganisationComponent} from './shared/privacy-organisation/privacy-organisation.component';
import {TransparencyComponent} from './shared/transparency/transparency.component';
import {ThirdPartyComponent} from './shared/third-party/third-party.component';
import {ForumsComponent} from './leaderboard/forum/forums/forums.component';
import {MessagesComponent} from './leaderboard/forum/messages/messages.component';
import {NewForumComponent} from './leaderboard/forum/new-forum/new-forum.component';
import {CKEditorModule} from '@ckeditor/ckeditor5-angular';
import {PortalComponent} from './leaderboard/portal/portal.component';
import {VendorBannerComponent} from './leaderboard/banner/vendor-banner/vendor-banner.component';
import {EnterpriseBannerComponent} from './leaderboard/banner/enterprise-banner/enterprise-banner.component';
import {LeaderboardV2Component} from './leaderboard/leaderboard-v2/leaderboard-v2.component';
import {RulesComponent} from './leaderboard/rules/rules.component';
import {PrizeComponent} from './leaderboard/prize/prize.component';
import {PasswordResetComponent} from './shared/password-reset/password-reset.component';
import {PasswordConfirmationComponent} from './shared/password-confirmation/password-confirmation.component';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {EnterpriseFreeTrialExpiredComponent} from './enterprise/free-trial-expired/free-trial-expired.component';
import {EnterpriseUsersComponent} from './enterprise/management/users/enterprise-users.component';
import {InviteUserDialogComponent} from './shared/invite-user-dialog/invite-user.dialog';
import {InviteMemberDialogComponent} from './shared/invite-member-dialog/invite-member.dialog';
import {ActiveUserComponent} from './enterprise/management/component/active-user/active-user.component';
import {TeamsComponent} from './vendor/teams/teams.component';
import {
  DownloadPredictionsComponent
} from './enterprise/enterprise-insights/component/download-predictions/download-predictions.component';
import {NgxExtendedPdfViewerModule} from 'ngx-extended-pdf-viewer';
// videogular
import {CoreModule} from '@videogular/ngx-videogular/core';
import {ControlsModule} from '@videogular/ngx-videogular/controls';
import {OverlayPlayModule} from '@videogular/ngx-videogular/overlay-play';
import {BufferingModule} from '@videogular/ngx-videogular/buffering';
// Bugsnag
import Bugsnag from '@bugsnag/js';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';
import { ProfileComponent } from './shared/profile/profile.component';
import {IframIAComponent} from './shared/ia-iframe/ia-iframe.component';
import { AskAiComponent } from './leaderboard/ask-ai/ask-ai.component';

Bugsnag.start({ apiKey: '1d6ead8cb76217e71e982f645d71bf2a' });

// create a factory which will return the Bugsnag error handler
export function errorHandlerFactory() {
  return new BugsnagErrorHandler();
}

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ResetComponent,
    HeaderComponent,
    FooterComponent,
    PaginationComponent,
    AlertDialogComponent,
    TermsDialogComponent,
    ConditionsDialogComponent,
    VideoDialogComponent,
    ProbaModeDialogComponent,
    InviteUserDialogComponent,
    InviteMemberDialogComponent,
    EnterpriseDatasetUploadComponent,
    EnterpriseDatasetConfigurationComponent,
    EnterpriseGenerateInsightsComponent,
    EnterpriseInsightDetailsComponent,
    EnterpriseDatasetsComponent,
    EnterpriseMlModelsComponent,
    EnterpriseInsightsComponent,
    DownloadPredictionsComponent,
    EnterpriseFreeTrialExpiredComponent,
    EnterpriseUsersComponent,
    ActiveUserComponent,
    SharedDatasetsComponent,
    PredictionsListComponent,
    PredictionsUploadComponent,
    RulesComponent,
    PrizeComponent,
    FilterPipe,
    SupportComponent,
    TestModelComponent,
    SignupComponent,
    PasswordResetComponent,
    PasswordConfirmationComponent,
    TermsComponent,
    TermsOrganisationComponent,
    PrivacyComponent,
    PrivacyOrganisationComponent,
    TransparencyComponent,
    ThirdPartyComponent,
    PortalComponent,
    VendorBannerComponent,
    EnterpriseBannerComponent,
    ForumsComponent,
    MessagesComponent,
    NewForumComponent,
    LeaderboardV2Component,
    TeamsComponent,
    ProfileComponent,
    IframIAComponent,
    AskAiComponent,
  ],
  entryComponents: [
    AlertDialogComponent,
    InviteUserDialogComponent,
    InviteMemberDialogComponent,
    ProbaModeDialogComponent,
    TermsDialogComponent,
    VideoDialogComponent,
  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    ReactiveFormsModule,
    FormsModule,
    NgbModule,
    AppRoutingModule,
    NgxFileDropModule,
    FontAwesomeModule,
    NgxPaginationModule,
    CKEditorModule,
    MatDialogModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatIconModule,
    MatCardModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatSelectModule,
    MatSnackBarModule,
    NgxExtendedPdfViewerModule,
    GoogleChartsModule.forRoot(),
    MDBBootstrapModule.forRoot(),
    NgCircleProgressModule.forRoot({
      // set defaults here
      radius: 50,
      outerStrokeWidth: 16,
      innerStrokeWidth: 8,
      // outerStrokeGradient: true,
      outerStrokeColor: '#009EB9',
      // outerStrokeGradientStopColor: '#e9467c',
      innerStrokeColor: '#C7E596',
      animationDuration: 300,
      titleColor: 'black',
      titleFontSize: '45',
      unitsColor: 'black',
      unitsFontSize: '45',
      subtitle: 'COMPLETE',
      subtitleColor: 'black',
      subtitleFontSize: '15',
      subtitleFontWeight: '200',
      animateTitle: true,
      showSubtitle: true,
      showTitle: true,
      startFromZero: false
    }),
    RecaptchaModule,
    RecaptchaFormsModule,
    BrowserAnimationsModule,
    CoreModule,
    ControlsModule,
    OverlayPlayModule,
    BufferingModule,
  ],
  providers: [
    {provide: ErrorHandler, useFactory: errorHandlerFactory},
    {provide: HTTP_INTERCEPTORS, useClass: TrustiiInterceptor, multi: true},
    FilterPipe,
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
